import {
  put,
  takeLatest,
  call,
  PutEffect,
  CallEffect,
} from 'redux-saga/effects'
import { AxiosError } from 'axios'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import UrlHelper from 'helpers/url'
import NoticeHelper from 'helpers/notice'
import { SHOW_NOTICE } from 'reducers/notice'
import { FeatureFlag } from 'types/featureFlags'
import * as ApiRouter from 'constants/apiRouting'
import {
  FETCH_FEATURE_FLAGS_TABLE,
  SUCCESS_FEATURE_FLAGS_TABLE,
  FAILURE_FEATURE_FLAGS_TABLE,
  LOAD_FEATURE_FLAGS_TABLE,
  FETCH_FEATURE_FLAGS_FORM,
  SUCCESS_FEATURE_FLAGS_FORM,
  FAILURE_FEATURE_FLAGS_FORM,
  LOAD_FEATURE_FLAGS_FORM,
  SET_FEATURE_FLAGS_INFO,
} from './reducer'

function* fetchFeatureFlagsTable(): Generator<
  | PutEffect<{ type: string; payload?: boolean | unknown | FeatureFlag[] }>
  | CallEffect
> {
  try {
    yield put({ type: LOAD_FEATURE_FLAGS_TABLE, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRouter.FEATURE_FLAGS,
    })

    if (!Object.prototype.hasOwnProperty.call(response, 'error')) {
      yield put({
        type: SUCCESS_FEATURE_FLAGS_TABLE,
        payload: response,
      })
    } else {
      yield put({ type: FAILURE_FEATURE_FLAGS_TABLE })
    }

    yield put({ type: LOAD_FEATURE_FLAGS_TABLE, payload: false })
  } catch (error) {
    console.error('LOAD FEATURE FLAGS TABLE ERROR', error)
  }
}

function* fetchFeatureFlagsForm({
  payload,
}: {
  type: string
  payload: { entityId: number }
}): Generator<
  | CallEffect
  | PutEffect<{ type: string; payload?: boolean | FeatureFlag | unknown }>
> {
  try {
    yield put({ type: LOAD_FEATURE_FLAGS_FORM, payload: true })

    const { entityId } = payload

    let response
    if (entityId) {
      response = yield call(fetchFromAPI, {
        headers: getDefaultAutorizedHeaders(),
        partUrl: UrlHelper.create(ApiRouter.FEATURE_FLAGS_INFO, {
          id: entityId,
        }),
      })
    }

    if (response && !Object.prototype.hasOwnProperty.call(response, 'error')) {
      yield put({
        type: SUCCESS_FEATURE_FLAGS_FORM,
        payload: response,
      })
    } else {
      yield put({ type: FAILURE_FEATURE_FLAGS_FORM })
    }

    yield put({ type: LOAD_FEATURE_FLAGS_FORM, payload: false })
  } catch (error) {
    console.error('LOAD FEATURE FLAGS FORM ERROR', error)
  }
}

function* setFeatureFlagsInfo({
  payload,
  promise,
}: {
  type: string
  payload: {
    entityId: number
    values: FeatureFlag
  }
  promise: {
    resolve: () => void
  }
}): Generator<
  CallEffect | PutEffect<{ type: string }>,
  unknown,
  { error: AxiosError; errors: string } | { id: number }
> {
  try {
    const { entityId, values } = payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRouter.FEATURE_FLAGS_INFO, { id: entityId }),
      method: 'PUT',
      data: values,
    })

    if ('error' in response) {
      return yield call(promise.resolve, response.errors)
    }

    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createSuccessObj('Feature flag change successful'),
    })

    return yield call(promise.resolve, false)
  } catch (error) {
    throw new Error(`UNKNOWN FEATURE FLAG CHANGE ERROR. DATA: ${error}`)
  }
}

function* featureFlagsSaga() {
  yield takeLatest(FETCH_FEATURE_FLAGS_TABLE, fetchFeatureFlagsTable)
  yield takeLatest(FETCH_FEATURE_FLAGS_FORM, fetchFeatureFlagsForm)
  yield takeLatest(SET_FEATURE_FLAGS_INFO, setFeatureFlagsInfo)
}

export default featureFlagsSaga
